interface ModuleRect {
  xM: number;
  yM: number;
  wM: number;
  hM: number;
  /** optionaler Verschattungsanteil 0..1 → tönt das Modul (Nahschatten v1.1). */
  shadedFraction?: number;
}

/** Hindernis-Footprint (Dach-Ebenen-Meter) für die Nahschatten-Darstellung. */
interface ObstacleRect {
  xM: number;
  yM: number;
  widthM: number;
  depthM: number;
  label?: string;
}

/** Schattenpolygon in Dach-Ebenen-Metern (worst-case Sonnenstand). */
interface ShadowPoly {
  points: { x: number; y: number }[];
}

interface RoofLayoutSvgProps {
  /** Dachfeld-Breite (Traufe), Meter. */
  roofWidthM: number;
  /** Dachfeld-Länge (Falllinie), Meter. */
  roofLengthM: number;
  /** platzierte Module als lokale Meter-Rechtecke. */
  modules: ModuleRect[];
  /** maximale Render-Breite in px. */
  maxWidthPx?: number;
  /** optionale Schattenpolygone (Nahschatten) als Overlay. */
  shadowPolygons?: ShadowPoly[];
  /** optionale Hindernis-Footprints (Kamin/Gaube). */
  obstacles?: ObstacleRect[];
}

/** Modulfarbe nach Verschattungsanteil: blau (frei) → orange → rot (stark verschattet). */
function moduleFill(shaded: number | undefined): string {
  if (shaded == null || shaded <= 0.05) return "#1e3a8a"; // frei
  if (shaded < 0.5) return "#d97706"; // teilverschattet
  return "#b91c1c"; // stark verschattet
}

/**
 * Rein darstellende SVG-Visualisierung der Modulbelegung — zeichnet das
 * rechteckige Dachfeld und die platzierten Module maßstäblich (viewBox in
 * Metern). Keine WebGL/Karte nötig; deterministisch und screenshot-fähig
 * (auch für das Konzept-PDF nutzbar). Berechnung kommt aus roof-core.
 *
 * Optional: Nahschatten-Overlay (Schattenpolygone + getönte Module) und
 * Hindernis-Footprints — alles im selben Meter-Frame wie die Module.
 */
export function RoofLayoutSvg({
  roofWidthM,
  roofLengthM,
  modules,
  maxWidthPx = 460,
  shadowPolygons,
  obstacles,
}: RoofLayoutSvgProps) {
  if (!(roofWidthM > 0) || !(roofLengthM > 0)) return null;

  const scale = maxWidthPx / roofWidthM;
  const widthPx = maxWidthPx;
  const heightPx = Math.max(40, roofLengthM * scale);
  const stroke = Math.max(roofWidthM * 0.0025, 0.012);

  return (
    <svg
      viewBox={`0 0 ${roofWidthM} ${roofLengthM}`}
      width={widthPx}
      height={heightPx}
      role="img"
      aria-label={`Modulbelegung: ${modules.length} Module auf ${roofWidthM.toFixed(1)}×${roofLengthM.toFixed(1)} m`}
      style={{ display: "block", maxWidth: "100%", height: "auto", borderRadius: 6 }}
    >
      {/* Dachfeld */}
      <rect
        x={0}
        y={0}
        width={roofWidthM}
        height={roofLengthM}
        fill="var(--color-bg-subtle, #f1f5f9)"
        stroke="var(--color-border, #cbd5e1)"
        strokeWidth={stroke}
      />

      {/* Schattenwurf (Nahschatten, worst-case Sonnenstand) — unter den Modulen */}
      {(shadowPolygons ?? [])
        .filter((s) => s.points && s.points.length >= 3)
        .map((s, i) => (
          <polygon
            key={`shadow-${i}`}
            points={s.points.map((p) => `${p.x},${p.y}`).join(" ")}
            fill="#1f2937"
            fillOpacity={0.22}
            stroke="#111827"
            strokeOpacity={0.3}
            strokeWidth={stroke}
          />
        ))}

      {/* Module (Tönung nach Verschattungsanteil) */}
      {modules.map((m, i) => (
        <rect
          key={i}
          x={m.xM}
          y={m.yM}
          width={m.wM}
          height={m.hM}
          fill={moduleFill(m.shadedFraction)}
          stroke="#60a5fa"
          strokeWidth={stroke}
          rx={Math.min(m.wM, m.hM) * 0.06}
        />
      ))}

      {/* Hindernis-Footprints (Kamin/Gaube) — über allem, klar markiert */}
      {(obstacles ?? [])
        .filter((o) => o.widthM > 0 && o.depthM > 0)
        .map((o, i) => (
          <g key={`obs-${i}`}>
            <rect
              x={o.xM}
              y={o.yM}
              width={o.widthM}
              height={o.depthM}
              fill="#374151"
              fillOpacity={0.85}
              stroke="#f9fafb"
              strokeWidth={stroke * 1.5}
            />
          </g>
        ))}
    </svg>
  );
}
